import { useWorkspace } from "../lib/RelayDataProvider";

/**
 * track:workspace-write-auth (Team Log #48220 Track A, Outcome 4) — this
 * hook used to read `relay_workspaces/{id}.tier` as the Agent Autonomy
 * setting (read_only/workspace/trusted/full), coercing anything else to
 * "workspace". That collided with the SAME field's billing meaning
 * (restApi.ts's `resolveWorkspaceTier`, the daemon's `isTokenLimitReached`
 * via GET /api/workspace/tier — both read `.tier` expecting a billing
 * value defaulting to "free") — choosing "Manual" in Workspace Governance
 * silently also dropped the workspace to the free-plan token budget and
 * hid the Invite button (track:workspace-controls-audit #48218's finding).
 *
 * `.tier` is billing-only as of this track. Agent Autonomy moved to its
 * own field — see useAgentAutonomyTier.ts.
 */
export interface WorkspaceTierValue {
  /** Billing tier — "free" by default. No other value is currently ever
   * set by product code; a workspace's Lead can, in principle, write
   * anything here via the governance/tier... no — via direct Firestore
   * access, which is why restApi.ts's resolveWorkspaceTier() distrusts a
   * bare "founder" claim from this field. Treat this as an opaque string,
   * not a closed enum — billing tiers beyond "free" aren't implemented yet. */
  tier: string;
  loading: boolean;
  error: Error | null;
  /** Forces an immediate re-read past the TTL cache — call after your own
   * write completes so the UI reflects it right away instead of waiting
   * out the cache window. See useWorkspace's refetch for why this exists. */
  refetch: () => Promise<void>;
}

/**
 * cost-audit fix (seq #29785) — this used to open its own onSnapshot
 * listener on relay_workspaces/{workspaceId}. Reads from the shared,
 * TTL-cached useWorkspace() instead (RelayDataProvider.tsx) so the
 * components that call this hook (HumanMembersSection, InviteCodeSection,
 * WorkspaceGovernanceSection) share one cached read instead of each
 * opening a real-time listener on the same doc.
 */
export function useWorkspaceTier(workspaceId: string): WorkspaceTierValue {
  const { data, loading, refetch } = useWorkspace(workspaceId);
  const tier = data.tier || "free";

  return { tier, loading, error: null, refetch };
}
